import { useState, useEffect, useCallback } from 'react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useAuth } from '../firebase/useAuth';
import { useApp } from '../context/AppContext';
import { CATEGORIES } from '../data/categories';

function todayDateStr() {
  return new Date().toISOString().slice(0, 10);
}

export function useTodayEntry() {
  const { user } = useAuth();
  const { state } = useApp();
  const { prompts, todayEntry } = state;

  const [drafts, setDrafts] = useState(() => {
    const init = {};
    CATEGORIES.forEach(c => { init[c.key] = ''; });
    return init;
  });

  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Per-category submitted state (individual submits)
  const [submittedCategories, setSubmittedCategories] = useState(new Set());

  // Sync drafts and submitted state when todayEntry loads from Firestore
  useEffect(() => {
    if (!todayEntry) return;
    const init = {};
    CATEGORIES.forEach(c => { init[c.key] = todayEntry[c.key]?.response || ''; });
    setDrafts(init);
    setSubmitted(!todayEntry.isDraft);
    const indivSubmitted = new Set(
      CATEGORIES.filter(c => todayEntry[c.key]?.submitted).map(c => c.key)
    );
    setSubmittedCategories(indivSubmitted);
  }, [todayEntry]);

  const updateDraft = useCallback((key, value) => {
    setDrafts(prev => ({ ...prev, [key]: value }));
  }, []);

  const saveDraft = useCallback(async () => {
    if (!user || !prompts) return;
    const date = todayDateStr();
    const data = {
      date,
      isDraft: true,
      updatedAt: serverTimestamp(),
    };
    CATEGORIES.forEach(c => {
      data[c.key] = {
        prompt: prompts[c.key] || '',
        response: drafts[c.key] || '',
        // Preserve submitted flag on auto-save
        ...(submittedCategories.has(c.key) ? { submitted: true } : {}),
      };
    });
    if (!todayEntry) data.createdAt = serverTimestamp();

    setSaving(true);
    await setDoc(doc(db, 'users', user.uid, 'entries', date), data, { merge: true });
    setSaving(false);
  }, [user, prompts, drafts, todayEntry, submittedCategories]);

  // Submit a single category — makes it visible in the feed immediately
  const submitCategory = useCallback(async (catKey) => {
    if (!user || !prompts) return;
    if (submitted || submittedCategories.has(catKey)) return;
    const date = todayDateStr();
    const categoryData = {
      prompt: prompts[catKey] || '',
      response: drafts[catKey] || '',
      submitted: true,
    };
    const base = {
      date,
      isDraft: true,
      updatedAt: serverTimestamp(),
    };
    if (!todayEntry) base.createdAt = serverTimestamp();
    await setDoc(
      doc(db, 'users', user.uid, 'entries', date),
      { ...base, [catKey]: categoryData },
      { merge: true }
    );
    setSubmittedCategories(prev => new Set([...prev, catKey]));
  }, [user, prompts, drafts, todayEntry, submitted, submittedCategories]);

  const submitEntry = useCallback(async () => {
    if (!user || !prompts) return;
    const date = todayDateStr();
    const data = {
      date,
      isDraft: false,
      updatedAt: serverTimestamp(),
    };
    CATEGORIES.forEach(c => {
      data[c.key] = {
        prompt: prompts[c.key] || '',
        response: drafts[c.key] || '',
        submitted: true,
      };
    });
    if (!todayEntry) data.createdAt = serverTimestamp();

    await setDoc(doc(db, 'users', user.uid, 'entries', date), data, { merge: true });
    setSubmitted(true);
    setSubmittedCategories(new Set(CATEGORIES.map(c => c.key)));
  }, [user, prompts, drafts, todayEntry]);

  const reopenEntry = useCallback(() => setSubmitted(false), []);

  const allFilled = CATEGORIES.every(c => drafts[c.key]?.trim());

  return {
    prompts,
    drafts,
    saving,
    submitted,
    submittedCategories,
    allFilled,
    updateDraft,
    saveDraft,
    submitCategory,
    submitEntry,
    reopenEntry,
  };
}
